import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EkonomiskAnalysKodbehorighet from './Components/EkonomiskAnalysKodbehorighet/EkonomiskAnalysKodbehorighet';
import EkonomiskAnalysDenominations from '../EkonomiskAnalysDenominations/EkonomiskAnalysDenominations';
import EkonomiskAnalysCodePrivilegesQuery from '../../Queries/EkonomiskAnalysCodePrivilegesQuery/EkonomiskAnalysCodePrivilegesQuery';
import { IFilledLocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  getUrvalText,
  NO_URVAL_TEXT,
} from '../SideMenu/Components/UrvalOchSummering/Components/Urval/Utils/UrvalUtils';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import InfoText from '../../../../../../../Components/InfoText/InfoText';
import {
  EkonomiskAnalysContext,
  EkonomiskAnalysContextConsumer,
} from '../Context/EkonomiskAnalysContext';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import AdvancedUrvalDialog from '../../../../../../../Components/Urval/Components/AdvancedUrvalDialog/AdvancedUrvalDialog';
import {
  EK_KONTO_URVAL_COLLECTION_NAME,
  EKONOMISK_ANALYS_URVAL_ROUTINE_ID,
} from '../../../../../../../Components/Urval/Consts/UrvalConsts';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { useBoolState } from '../../../../../../../Hooks/booleanHooks';
import RutinUrvalListQuery from '../../../../../../../Components/Urval/Components/SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery';
import {
  EkonomiskAnalysIngaendeKodbehQueryVariables,
  EkonomiskAnalysIngaendeKodbehQuery,
} from './Queries/_QueryTypes/EkonomiskAnalysIngaendeKodbehQuery';
import IngaendeKodbehQuery from './Queries/EkonomiskAnalysIngaendeKodbehQuery.gql';
import KodbehorighetInfo from '../../../../../../../Components/KodbehorighetInfo/KodbehorighetInfo';
import { getComparisonString } from '../../Utils/analysUtils';

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      rightSideContainer: {
        alignSelf: 'flex-end',
        marginBottom: '-7px',
      },
      spacingRight: { paddingRight: 16 },
      inline: { display: 'inline', whiteSpace: 'nowrap' },
      alignStart: { alignSelf: 'flex-start' },
      hidden: { visibility: 'hidden' },
      infoContainer: {
        display: 'flex',
        flexDirection: 'column',
      },
      urvalTextContainer: {
        display: 'flex',
        maxWidth: '430px',
        alignItems: 'center',
      },
      clickable: {
        cursor: 'pointer',
      },
      clickableObject: {
        cursor: 'pointer',
        '& p:first-of-type': {
          color: theme.palette.primary.main,
        },
        '&:hover p:first-of-type, &.accessibility p:first-of-type': {
          borderBottom: '2px solid ' + theme.palette.primary.main,
          marginBottom: -2,
        },
      },
    } as const)
);

interface IProps {
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  analysSettings: IFilledLocalAnalys;
  denomination: number;
  showComparsion: boolean;
  updatedAnalysSettingsUrval: IUIUrval | null;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  onDenominationChange: (denomination: number) => void;
  selectedAnalys: ISelectedAnalys;
  hide?: boolean;
  rightSideComponent?: React.ReactElement<any>;
  placeHolder?: string;
  sammanfattningsMode?: boolean;
}

/**
 * Meny som ligger ovanför anlaystabellen med information om användarens valda analys.
 */
const EkonomiskAnalysDetailInfo: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles();
  const {
    kodbehorighetsUrval,
    grundurvalUrval,
    urval,
    setUrval,
    codeSystemId,
    accessibilityMode,
    userInfo,
  } = useContext(EkonomiskAnalysContext);

  const ingaendeKodbehQueryResult = useQuery<
    EkonomiskAnalysIngaendeKodbehQuery,
    EkonomiskAnalysIngaendeKodbehQueryVariables
  >(IngaendeKodbehQuery, {
    variables: {
      kodplanId: codeSystemId,
      kodbehId: props.selectedAnalys.codePrivilege.id,
    },
  });

  const [urvalOpen, setUrvalOpen, setUrvalClose] = useBoolState(false);

  if (props.hide) {
    return null;
  }

  const hasIngaendeKodbeh =
    ingaendeKodbehQueryResult &&
    ingaendeKodbehQueryResult.data &&
    ingaendeKodbehQueryResult.data.ekingaendekodbehorigheter != null &&
    ingaendeKodbehQueryResult.data.ekingaendekodbehorigheter.length > 1;

  return (
    <EkonomiskAnalysContextConsumer>
      {({ hasWholeCodeBook }) => {
        if (ingaendeKodbehQueryResult.loading) {
          return null;
        }

        const kodbehorighetNamn =
          kodbehorighetsUrval && kodbehorighetsUrval.savedUrvalProps
            ? kodbehorighetsUrval.savedUrvalProps.namn
            : undefined;

        const rightSideComponent = props.rightSideComponent
          ? props.rightSideComponent
          : null;

        return (
          <EkonomiskAnalysCodePrivilegesQuery
            anvandsFor={props.analysSettings.anvandsFor}
            skip={!hasWholeCodeBook}
          >
            {codePrivileges => {
              const egetUrval = props.updatedAnalysSettingsUrval
                ? getUrvalText(props.updatedAnalysSettingsUrval)
                : NO_URVAL_TEXT;
              const urvalTooltip = props.sammanfattningsMode
                ? 'Redigering kan inte göras i rapportkategori Detaljanalyser'
                : `${egetUrval}. Klicka för att Visa/Redigera urval`;

              return (
                <div className={classes.container}>
                  <div className={classes.infoContainer}>
                    <EDTooltip title={urvalTooltip}>
                      <div
                        className={cx(classes.urvalTextContainer, {
                          ['accessibility']: accessibilityMode,
                        })}
                      >
                        <EDTypography
                          onClick={
                            props.sammanfattningsMode ? undefined : setUrvalOpen
                          }
                          variant="body2"
                          className={cx({
                            [classes.clickable]: !props.sammanfattningsMode,
                          })}
                          color="textPrimary"
                          bold
                        >
                          Eget urval:
                        </EDTypography>
                        &nbsp;
                        <EDTypography
                          variant="body2"
                          color="textPrimary"
                          onClick={
                            props.sammanfattningsMode ? undefined : setUrvalOpen
                          }
                          noWrap
                        >
                          {egetUrval}
                        </EDTypography>
                        <EDIconButton
                          color="primary"
                          onClick={
                            props.sammanfattningsMode ? undefined : setUrvalOpen
                          }
                          tooltip=""
                          icon="filter_list"
                          margin="dense"
                          padding="dense"
                          noTopMargin
                          noBottomMargin
                          disabled={props.sammanfattningsMode}
                        />
                      </div>
                    </EDTooltip>
                    <RutinUrvalListQuery
                      codeSystemID={codeSystemId}
                      collectionName={EK_KONTO_URVAL_COLLECTION_NAME}
                      routineID={EKONOMISK_ANALYS_URVAL_ROUTINE_ID}
                      kodplanUrval={true}
                    >
                      {({
                        rutinUrvalList,
                        variables,
                        loading: rutinUrvalListLoading,
                      }) =>
                        urvalOpen &&
                        kodbehorighetsUrval &&
                        !rutinUrvalListLoading ? (
                          <AdvancedUrvalDialog
                            urval={urval}
                            setUrval={setUrval}
                            collectionName={EK_KONTO_URVAL_COLLECTION_NAME}
                            routineID={EKONOMISK_ANALYS_URVAL_ROUTINE_ID}
                            codeSystemID={codeSystemId}
                            onClose={setUrvalClose}
                            open={urvalOpen}
                            kodplanUrval={true}
                            kodbehorigheturval={kodbehorighetsUrval}
                            rutinUrvalList={rutinUrvalList}
                            rutinUrvalVariables={variables}
                            placeHolder={'Spara för att namnge urvalet'}
                          />
                        ) : (
                          <></>
                        )
                      }
                    </RutinUrvalListQuery>
                    <span style={{ display: 'flex' }}>
                      <div className={classes.spacingRight}>
                        {props.selectedAnalys.codePrivilege.name && (
                          <div
                            className={cx(classes.spacingRight, {
                              [classes.clickableObject]:
                                hasWholeCodeBook || hasIngaendeKodbeh,
                              ['accessibility']: accessibilityMode,
                            })}
                          >
                            <InfoText
                              dialogTitle={`Urval för ${
                                kodbehorighetNamn
                                  ? kodbehorighetNamn
                                  : 'Okänt namn'
                              }`}
                              buttonSize="medium"
                              tooltipText="Visa urval för kodbehörigheten"
                              text={
                                <EkonomiskAnalysKodbehorighet
                                  impersonatedCodePrivilege={
                                    props.impersonatedCodePrivilege
                                  }
                                  anvandsFor={props.analysSettings.anvandsFor}
                                  updateImpersonatedCodePrivilege={
                                    props.updateImpersonatedCodePrivilege
                                  }
                                  ingaendeKodbehorigheter={ingaendeKodbehQueryResult.data?.ekingaendekodbehorigheter?.map(
                                    kodbeh => ({
                                      name: kodbeh.name,
                                      id: kodbeh.kodbeh_id,
                                    })
                                  )}
                                  hasWholeBook={hasWholeCodeBook}
                                  codePrivileges={codePrivileges}
                                  selectedAnalys={props.selectedAnalys}
                                  kodbehorighetNamn={kodbehorighetNamn}
                                />
                              }
                            >
                              {kodbehorighetsUrval && (
                                <KodbehorighetInfo
                                  kodbehorigheturval={[kodbehorighetsUrval]}
                                />
                              )}
                            </InfoText>
                          </div>
                        )}
                        {props.selectedAnalys.analys.grundurval &&
                          props.selectedAnalys.analys.grundurval.name && (
                            <InfoText
                              title="Grundurval:"
                              dialogTitle={
                                props.selectedAnalys.analys.grundurval.name
                              }
                              buttonSize="medium"
                              text={props.selectedAnalys.analys.grundurval.name}
                              tooltipText="Visa urval för kodbehörigheten"
                            >
                              {grundurvalUrval && (
                                <KodbehorighetInfo
                                  kodbehorigheturval={[grundurvalUrval]}
                                />
                              )}
                            </InfoText>
                          )}
                      </div>
                      <div
                        className={cx(classes.spacingRight, classes.alignStart)}
                      >
                        <div
                          className={cx(
                            classes.inline,
                            classes.clickableObject,
                            { ['accessibility']: accessibilityMode }
                          )}
                        >
                          <EkonomiskAnalysDenominations
                            denomination={props.denomination}
                            onChange={props.onDenominationChange}
                          />
                        </div>

                        <div
                          className={cx(classes.spacingRight, {
                            [classes.hidden]: !props.showComparsion,
                          })}
                        >
                          <EDTypography
                            variant="body2"
                            className={classes.inline}
                            color="textPrimary"
                            bold
                          >
                            {`Jämförelsetal: `}
                          </EDTypography>
                          <EDTypography
                            variant="body2"
                            className={classes.inline}
                            color="textPrimary"
                          >
                            {getComparisonString(
                              props.analysSettings.dates,
                              props.analysSettings.isInterval,
                              props.analysSettings.dateInputType,
                              userInfo?.preferHelarsjamforelse
                            )}
                          </EDTypography>
                        </div>
                      </div>
                    </span>
                  </div>
                  <span className={classes.rightSideContainer}>
                    {rightSideComponent}
                  </span>
                </div>
              );
            }}
          </EkonomiskAnalysCodePrivilegesQuery>
        );
      }}
    </EkonomiskAnalysContextConsumer>
  );
};

export default EkonomiskAnalysDetailInfo;
